/**
 * Ads (`/ads`, docs/SPEC.md §14.1): the organisation's ads newest first —
 * the first scene's picture, the title, the state, length and aspect, when.
 * The picture is a frame the player draws in the browser from the ad's
 * manifest (P28-T10): lazily, once the tile comes into view, and one ad at a
 * time (`frames.ts` draws one frame at a time with one offscreen player).
 */
import { useRef } from "react";
import { Link } from "react-router-dom";
import { type AdSummary, useAds } from "../api/ads";
import { ADS, STATE_LABELS } from "../copy/brief";
import { sentenceFor } from "../copy/errors";
import { frameSize } from "../film/frames";
import { useManifestFrame } from "../film/useFrame";
import type { Aspect } from "../api/types";
import { useInView } from "../ui/useInView";
import { formatRelative } from "../ui/format";
import { useNow } from "../ui/useNow";
import { Button, Card, PageHeader, Pill, type PillTone, Skeleton } from "../ui";

const TONES: Record<AdSummary["state"], PillTone> = {
  brief: "purple",
  building: "warning",
  ready: "success",
  failed: "danger",
};

function ratio(aspect: string | null): string {
  if (aspect === "16:9") return "16 / 9";
  if (aspect === "1:1") return "1 / 1";
  return "9 / 16";
}

/** The tile's picture: 96 px wide at most, drawn at twice that. */
const TILE_SHORT = 192;

function AdTile({ ad, now }: { ad: AdSummary; now: number }) {
  const ref = useRef<HTMLLIElement>(null);
  const inView = useInView(ref);
  const aspect = (ad.aspect ?? "9:16") as Aspect;
  const frame = useManifestFrame(
    ad.manifest_url,
    null,
    frameSize(aspect, TILE_SHORT),
    inView && !!ad.manifest_url,
  );
  return (
    <li data-testid={`ad-${ad.id}`} ref={ref}>
      <Link
        to={`/ads/${encodeURIComponent(ad.id)}`}
        className="tb-card tb-card-plain flex h-full gap-4 p-3 no-underline transition-shadow hover:shadow-card-lg"
      >
        <span
          className="block w-20 shrink-0 self-start overflow-hidden rounded bg-slate-100 sm:w-24"
          style={{ aspectRatio: ratio(ad.aspect) }}
        >
          {frame ? (
            <img
              src={frame}
              alt=""
              className="h-full w-full object-cover"
              data-testid={`ad-frame-${ad.id}`}
            />
          ) : ad.manifest_url ? null : (
            <span className="flex h-full w-full items-center justify-center p-1 text-center text-[11px] text-slate-400">
              {ADS.noFilm}
            </span>
          )}
        </span>
        <span className="flex min-w-0 flex-col gap-1.5">
          <span className="break-words font-semibold text-slate-900">
            {ad.title || ADS.untitled}
          </span>
          <span>
            <Pill size="sm" tone={TONES[ad.state]}>
              {STATE_LABELS[ad.state]}
            </Pill>
          </span>
          <span className="text-xs text-slate-500">
            {[ad.length_s ? `${ad.length_s} s` : null, ad.aspect]
              .filter(Boolean)
              .join(" · ")}
          </span>
          <time
            className="text-xs text-slate-500"
            dateTime={ad.created_at}
            title={ad.created_at}
          >
            {formatRelative(ad.created_at, now)}
          </time>
        </span>
      </Link>
    </li>
  );
}

export function AdsPage() {
  const ads = useAds();
  const now = useNow(60_000);
  const items = ads.data?.items ?? [];
  return (
    <>
      <PageHeader
        eyebrow={ADS.eyebrow}
        title={ADS.title}
        right={
          <Button as={Link} to="/new" variant="primary">
            {ADS.newAd}
          </Button>
        }
      />
      {ads.isPending ? (
        <Card aria-busy="true" className="flex flex-col gap-3 p-6">
          <Skeleton height={14} />
          <Skeleton height={14} width="80%" />
        </Card>
      ) : ads.isError ? (
        <Card role="alert" className="p-6 text-slate-700">
          {sentenceFor(ads.error)}
        </Card>
      ) : items.length === 0 ? (
        <Card className="flex flex-col items-start gap-4 p-8">
          <p className="m-0 text-lg font-semibold text-slate-800">
            {ADS.empty}
          </p>
          <p className="m-0 text-slate-600">{ADS.emptyHint}</p>
          <Button as={Link} to="/new" variant="primary">
            {ADS.first}
          </Button>
        </Card>
      ) : (
        <ul className="m-0 grid list-none gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((ad) => (
            <AdTile key={ad.id} ad={ad} now={now} />
          ))}
        </ul>
      )}
    </>
  );
}
